import type { Message } from '../types';

export type MentionKind = 'program' | 'report' | 'diagram' | 'version';

export interface MentionCandidate {
  /** Token inserted into the prompt, without the leading `@`. */
  token: string;
  label: string;
  kind: MentionKind;
  /** One-line context shown in the picker. */
  detail?: string;
}

export interface SlashCommand {
  name: string;
  summary: string;
  /** Text substituted into the composer when the command is chosen. */
  expansion: string;
}

/** Only the trailing token is a trigger; `a@b` in prose must not open a picker. */
const MENTION_TRIGGER = /(?:^|\s)@([^\s@]*)$/;
const SLASH_TRIGGER = /^\/([a-z-]*)$/i;

export function readMentionQuery(textBeforeCaret: string): string | null {
  const match = textBeforeCaret.match(MENTION_TRIGGER);
  return match ? match[1] : null;
}

export function readSlashQuery(text: string): string | null {
  const match = text.match(SLASH_TRIGGER);
  return match ? match[1] : null;
}

function pushUnique(list: MentionCandidate[], candidate: MentionCandidate): void {
  if (list.some(item => item.token === candidate.token)) return;
  list.push(candidate);
}

/**
 * Things in this conversation that a follow-up can point at.
 *
 * Referring to "the program" or "that report" in prose forces the model to guess
 * which one; naming the artifact makes the reference exact, which matters most
 * once a session holds several revisions of the same block.
 */
export function collectMentionCandidates(messages: Message[]): MentionCandidate[] {
  const candidates: MentionCandidate[] = [];

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== 'assistant') continue;

    const versions = message.repairAppendix?.codeVersions || [];
    for (const version of versions) {
      if (!version?.code?.trim()) continue;
      pushUnique(candidates, {
        token: version.label || version.id,
        label: version.label || version.id,
        kind: 'version',
        detail: version.role === 'repair' ? '修复版本' : '基线版本',
      });
    }

    if (message.parsed?.code?.trim()) {
      pushUnique(candidates, { token: '当前程序', label: '当前程序', kind: 'program', detail: 'PLC 程序' });
    }
    if (message.parsed?.summary?.trim()) {
      pushUnique(candidates, { token: '实现说明', label: '实现说明', kind: 'report', detail: '本轮总结' });
    }
    if (message.parsed?.verification?.trim()) {
      pushUnique(candidates, { token: '安全验证', label: '安全验证', kind: 'report', detail: '安全与验证报告' });
    }
    if (message.parsed?.faults?.trim()) {
      pushUnique(candidates, { token: '故障寄存表', label: '故障寄存表', kind: 'report' });
    }
    if (message.parsed?.flowchart?.trim() || /```\s*mermaid/i.test(message.content || '')) {
      pushUnique(candidates, { token: '控制逻辑图', label: '控制逻辑图', kind: 'diagram' });
    }
  }

  return candidates.slice(0, 12);
}

export function filterMentions(candidates: MentionCandidate[], query: string): MentionCandidate[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return candidates;
  return candidates.filter(candidate =>
    candidate.token.toLowerCase().includes(normalized)
    || candidate.label.toLowerCase().includes(normalized));
}

/**
 * Composer commands.
 *
 * These expand into plain prompt text rather than invoking anything directly:
 * the pipeline stays the single path a request travels, and the user can still
 * see and edit what is about to be sent.
 */
export const SLASH_COMMANDS: SlashCommand[] = [
  {
    name: 'validate',
    summary: '重新校验当前程序',
    expansion: '请对 @当前程序 重新执行安全与语法校验，列出每条问题及其所在行。',
  },
  {
    name: 'diff',
    summary: '对比最近两个版本',
    expansion: '请对比最近两个版本的程序，说明每处改动的工程含义与安全影响。',
  },
  {
    name: 'explain',
    summary: '逐段解释当前程序',
    expansion: '请逐段解释 @当前程序 的控制逻辑，重点说明状态机与安全互锁。',
  },
  {
    name: 'testcases',
    summary: '生成现场测试用例',
    expansion: '请基于 @当前程序 给出现场调试测试用例，覆盖急停、故障复位和边界工况。',
  },
  {
    name: 'assumptions',
    summary: '列出全部工程假设',
    expansion: '请列出 @当前程序 依赖的全部工程假设，并标注哪些需要现场确认。',
  },
];

export function filterSlashCommands(query: string): SlashCommand[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return SLASH_COMMANDS;
  return SLASH_COMMANDS.filter(command => command.name.startsWith(normalized));
}

/** Replace the in-progress `@query` before the caret with the chosen token. */
export function applyMention(text: string, caret: number, token: string): { text: string; caret: number } {
  const before = text.slice(0, caret);
  const match = before.match(MENTION_TRIGGER);
  if (!match) return { text, caret };
  const start = before.length - match[0].length + (match[0].startsWith('@') ? 0 : 1);
  const inserted = `@${token} `;
  const next = `${text.slice(0, start)}${inserted}${text.slice(caret)}`;
  return { text: next, caret: start + inserted.length };
}
